import PromiseWorker from 'promise-worker';

import { TileNode } from '../loader/cesium/tile-set/types';
import type { CesiumPntsWebworkerResponse } from './fetch-cesium-pnts';
import type { ChunkData } from './fetch-chunks';
import type { DxfWebworkerResponse } from './fetch-dxf';
import type { PointcloudChunkData } from './fetch-pointcloud-chunks';
import type { TiledModelWebworkerResponse } from './fetch-tiled-model';
import type { TriangulationWebworkerResponse } from './triangulate';
import {
  FetchCesiumPntsWorker,
  FetchChunksWorker,
  FetchDxfWorker,
  FetchPointcloudChunksWorker,
  FetchTiledModelWorker,
  TriangulateWorker,
} from './worker-imports';

export type WorkerType =
  | 'gltftChunk'
  | 'pointcloudChunk'
  | 'cesiumPnts'
  | 'tiledModels'
  | 'dxf'
  | 'triangulation';

interface WorkerWrapper<TExecuteArgs = unknown, TResponse = unknown> {
  worker: PromiseWorker;
  execute: (args: TExecuteArgs) => Promise<TResponse>;
  count: number;
}

export class WebWorkerManager {
  private workers: Partial<
    Record<
      WorkerType,
      | WorkerWrapper<unknown[], ChunkData[]>
      | WorkerWrapper<{ chunks: unknown[] }, PointcloudChunkData[]>
      | WorkerWrapper<unknown[], CesiumPntsWebworkerResponse[]>
      | WorkerWrapper<TileNode[], TiledModelWebworkerResponse[]>
      | WorkerWrapper<{ dxfS3URL: string; origin?: unknown }, DxfWebworkerResponse>
      | WorkerWrapper<{ points: number[][]; edges: number[][] }, TriangulationWebworkerResponse>
    >
  > = {};

  /**
   * Initialize workers based on the typeId.
   * @param {WorkerType | undefined} typeId - Determine which workers should be initialized. If undefined, all workers are initialized.
   */
  init(typeId?: WorkerType): void {
    if (!typeId || typeId === 'gltftChunk') {
      const worker = new PromiseWorker(new FetchChunksWorker());
      this.workers.gltftChunk = {
        worker,
        execute: (chunks: unknown) => worker.postMessage({ chunks }),
        count: 0,
      };
    }

    if (!typeId || typeId === 'pointcloudChunk') {
      const worker = new PromiseWorker(new FetchPointcloudChunksWorker());
      this.workers.pointcloudChunk = {
        worker,
        execute: (chunks: unknown) => worker.postMessage({ chunks }),
        count: 0,
      };
    }

    if (!typeId || typeId === 'cesiumPnts') {
      const worker = new PromiseWorker(new FetchCesiumPntsWorker());
      this.workers.cesiumPnts = {
        worker,
        execute: (chunks: unknown) => worker.postMessage({ chunks }),
        count: 0,
      };
    }

    if (!typeId || typeId === 'tiledModels') {
      const worker = new PromiseWorker(new FetchTiledModelWorker());
      this.workers.tiledModels = {
        worker,
        execute: (chunks: unknown) => worker.postMessage({ chunks }),
        count: 0,
      };
    }

    if (!typeId || typeId === 'dxf') {
      const worker = new PromiseWorker(new FetchDxfWorker());
      this.workers.dxf = {
        worker,
        execute: (args: unknown) => {
          const { dxfS3URL, origin } = args as { dxfS3URL: string; origin?: unknown };
          return worker.postMessage({ dxfS3URL, origin });
        },
        count: 0,
      };
    }

    if (!typeId || typeId === 'triangulation') {
      const worker = new PromiseWorker(new TriangulateWorker());
      this.workers.triangulation = {
        worker,
        execute: (args: unknown) => {
          const { points, edges } = args as { points: number[][]; edges: number[][] };
          return worker.postMessage({ points, edges });
        },
        count: 0,
      };
    }
  }

  spawnChunkLoader(chunks: unknown[]): Promise<ChunkData[]> {
    const worker = this.workers.gltftChunk as WorkerWrapper<unknown[], ChunkData[]> | undefined;
    if (chunks.length <= 0 || !worker) {
      return Promise.resolve([]);
    }

    worker.count += chunks.length;
    return worker.execute(chunks).then((response: ChunkData[]) => {
      worker.count -= response ? response.length : 0;
      return response;
    });
  }

  spawnPointcloudLoader(chunks: unknown[]): Promise<PointcloudChunkData[]> {
    const worker = this.workers.pointcloudChunk as
      | WorkerWrapper<{ chunks: unknown[] }, PointcloudChunkData[]>
      | undefined;
    if (chunks.length <= 0 || !worker) {
      return Promise.resolve([]);
    }

    worker.count += chunks.length;
    return worker.execute({ chunks }).then((response: PointcloudChunkData[]) => {
      worker.count -= response ? response.length : 0;
      return response;
    });
  }

  spawnCesiumPnts(chunks: unknown[]): Promise<CesiumPntsWebworkerResponse[]> {
    const worker = this.workers.cesiumPnts as
      | WorkerWrapper<unknown[], CesiumPntsWebworkerResponse[]>
      | undefined;
    if (chunks.length <= 0 || !worker) {
      return Promise.resolve([]);
    }

    worker.count += chunks.length;
    return worker.execute(chunks).then((response: CesiumPntsWebworkerResponse[]) => {
      worker.count -= response ? response.length : 0;
      return response;
    });
  }

  spawnTiledModelLoader(chunks: TileNode[]): Promise<TiledModelWebworkerResponse[]> {
    const worker = this.workers.tiledModels as
      | WorkerWrapper<TileNode[], TiledModelWebworkerResponse[]>
      | undefined;
    if (chunks.length <= 0 || !worker) {
      return Promise.resolve([]);
    }

    worker.count += chunks.length;
    return worker.execute(chunks).then((response: TiledModelWebworkerResponse[]) => {
      worker.count -= response ? response.length : 0;
      return response;
    });
  }

  spawnDxfLoader(dxfS3URL: string, origin?: unknown): Promise<DxfWebworkerResponse> {
    const worker = this.workers.dxf as
      | WorkerWrapper<{ dxfS3URL: string; origin?: unknown }, DxfWebworkerResponse>
      | undefined;
    if (!worker) {
      return Promise.reject(new Error('DXF worker not initialized'));
    }

    worker.count += 1;
    return worker.execute({ dxfS3URL, origin }).then((response: DxfWebworkerResponse) => {
      worker.count -= 1;
      return response;
    });
  }

  spawnTriangulationWorker(
    points: number[][],
    edges: number[][],
  ): Promise<TriangulationWebworkerResponse> {
    const worker = this.workers.triangulation as
      | WorkerWrapper<{ points: number[][]; edges: number[][] }, TriangulationWebworkerResponse>
      | undefined;
    if (!worker) {
      return Promise.reject(new Error('Triangulation worker not initialized'));
    }

    worker.count += 1;
    const result: Promise<TriangulationWebworkerResponse> = worker.execute({ points, edges });
    return result.then((response: TriangulationWebworkerResponse) => {
      worker.count -= 1;
      // eslint-disable-next-line @typescript-eslint/no-unsafe-return
      return response;
    });
  }

  isLoading(): boolean {
    const workerValues = Object.values(this.workers);
    return workerValues.some((worker): boolean => {
      return worker !== undefined && worker.count !== 0;
    });
  }

  /**
   * Abort the web workers defined by typeId
   * @param {WorkerType | undefined} typeId - Determine which workers should be stopped. If undefined, all workers are stopped.
   */
  stopChunks(typeId?: WorkerType): void {
    const workersToStop: WorkerType[] = typeId
      ? [typeId]
      : ['gltftChunk', 'pointcloudChunk', 'cesiumPnts', 'tiledModels', 'dxf', 'triangulation'];

    workersToStop.forEach((id) => {
      const worker = this.workers[id];
      if (worker) {
        // @ts-expect-error - PromiseWorker internal API
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        worker.worker._worker.terminate();
        delete this.workers[id];
      }
    });
  }
}

let wwManager: WebWorkerManager | undefined;
export function getWebWorkerManager(): WebWorkerManager {
  if (!wwManager) {
    wwManager = new WebWorkerManager();
  }
  return wwManager;
}
